import { Component, Input, EventEmitter, Output, OnInit } from '@angular/core';

import { ButtonTypesEnum } from '../../enums/button-types.enum';
import { ControlValueAccessorModel, getProvider } from '../../models/control-value-accessor.model';
import { AgprComponentStyleEnum } from '../../../Basic/enums/social-analysis-types.enum';
import { ActionKeys } from '../../../Forms/enums/keys.enum';

/**
* ### Accessibility Usage
* + ***ariaDescribedBy*** - use this if the text field has an external error message. Use message's id
* + ***ariaInvalid*** - use this to indicate state if the text field can fail in certain scenario
* + ***id*** - provide an unique id so that this component can be correctly recognized by screen readers. Use interpolation [id]="'unique_id'"
*/
@Component({
    selector: 'agpr-inplace',
    templateUrl: './inplace.component.html',
    styleUrls: ['./inplace.component.scss'],
    providers: [getProvider(InplaceComponent)],
    standalone: false
})
export class InplaceComponent extends ControlValueAccessorModel<string> implements OnInit {
    readonly agprComponentStyle = AgprComponentStyleEnum;

    @Input() public id: string;
    @Input() public isEditing: boolean = false;
    @Input() public placeholder: string;
    @Input() public tooltip: string;
    @Input() public tooltipPosition: string = 'right';
    @Input() public maxLength: number;
    @Input() public readonly: boolean = false;

    @Input() style: AgprComponentStyleEnum = AgprComponentStyleEnum.social;
    @Input() ariaInvalid: boolean;
    @Input() ariaDescribedBy: string;

    // `change`, `keyup` and `keydown` are handled automatically by angular + browser
    @Output() blur: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() focus: EventEmitter<FocusEvent> = new EventEmitter<FocusEvent>();
    @Output() keydown: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();
    @Output() keyup: EventEmitter<KeyboardEvent> = new EventEmitter<KeyboardEvent>();

    tempValue: string;
    readonly buttonTypes = ButtonTypesEnum;

    buttonId: string;

    ngOnInit(): void {
        this.buttonId = this.id + '_editButton';
    }

    edit() {
        if (this.readonly) {
            return;
        }
        this.tempValue = this.value;
        this.isEditing = true;
    }

    applyChanges() {
        this.tempValue = this.tempValue?.trim();
        this.writeValue(this.tempValue);
        this.onModelChange(this.tempValue);
        this.isEditing = false;
    }

    keyDown(event): void {
        if (event.key === ActionKeys.ENTER) {
            this.applyChanges();
        } else if (event.key === ActionKeys.ESCAPE) {
            event.stopImmediatePropagation();
            this.isEditing = false;
        } else if (event.key === ActionKeys.TAB) {
            event.preventDefault();
            this.isEditing = false;
        }
        if (!this.isEditing) {
            // If we have closed the editor we need to refocus the edit button, otherwise the focus leaves the widget
            requestAnimationFrame(() => document.getElementById(this.buttonId).focus());
        }
    }
}
